import { useState } from "react";
import Typography from "@mui/material/Typography";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import QuestionMark from "@mui/icons-material/QuestionMark";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { actSubjects } from "../../actprep-ui/constants/program";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { capitalize } from "../../actprep-ui/utils/formatting";
import api from "../../actprep-ui/utils/api";
import { useTimedLoading } from "../../actprep-ui/hooks/feedback/useTimedLoading";
import { LoadingButton } from "@mui/lab";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";

function OrganizationAccordionItem({ organization }) {
  const theme = useTheme();
  const { loading, setLoading } = useTimedLoading();
  const [startSubjectToProgramMapper, setStartSubjectToProgramMapper] =
    useState(
      Object.fromEntries(
        actSubjects.map((subject) => {
          const program = organization.programs.find(
            (program) => program.start_subject == subject
          );
          if (program) {
            return [
              subject,
              {
                label: program.program.title,
                uuid: program.program.uuid,
              },
            ];
          }
          return [subject, null];
        })
      )
    );

  const handleDeleteProgramAssignment = async (subject) => {
    await api.delete(apiUrls.admin.organizationToProgram(), {
      data: {
        organization_uuid: organization.uuid,
        start_subject: subject,
      },
    });
  };

  const handleRunAutomation = async () => {
    setLoading(true);
    const response = await api.post(
      apiUrls.admin.organizationToProgramAutomation(),
      {
        organization_uuid: organization.uuid,
      }
    );
    setLoading(false);
    if (response.status == 200) {
      showSuccessSnackbar("Automation successful!");
    }
  };

  const handleUpdateProgramAssignment = async (newProgram, subject) => {
    if (!newProgram) {
      handleDeleteProgramAssignment(subject);
      return;
    }

    await api.post(apiUrls.admin.organizationToProgram(), {
      organization_uuid: organization.uuid,
      program_uuid: newProgram.uuid,
      start_subject: subject,
    });
  };

  return (
    <Accordion
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography sx={{ width: "33%", flexGrow: 1 }}>
          {organization.name}
        </Typography>
        <Stack direction={"row"} spacing={1} mr={1}>
          {actSubjects.map((subject, i) => (
            <Chip
              label={subject[0].toUpperCase()}
              key={i}
              color={startSubjectToProgramMapper[subject] ? "success" : "error"}
              size="small"
            />
          ))}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          backgroundColor: theme.palette.background.secondary,
        }}
      >
        <Typography variant="subtitle1" fontWeight={"bold"}>
          Assign programs to start subjects
        </Typography>
        <Grid container spacing={1} mt={1}>
          {actSubjects.map((subject, i) => (
            <Grid size={6} key={i}>
              <ModelAutoComplete
                modelName={"Program"}
                label={`${capitalize(subject)} Start`}
                value={startSubjectToProgramMapper[subject]}
                setValue={(newValue) => {
                  handleUpdateProgramAssignment(newValue, subject);
                  setStartSubjectToProgramMapper((curr) => ({
                    ...curr,
                    [subject]: newValue,
                  }));
                }}
              />
            </Grid>
          ))}
        </Grid>
        <Stack direction={"row"} mt={2}>
          <LoadingButton
            size="small"
            variant="outlined"
            sx={{ flexGrow: 1 }}
            loading={loading}
            onClick={handleRunAutomation}
          >
            Run Automation
          </LoadingButton>
          <Tooltip
            title={`Assign all students in the ${organization.name} organization to the
          correct programs based on their cohort's start subject and start date.`}
          >
            <IconButton size="small">
              <QuestionMark />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
export default function OrgToProgramAutomationEditor() {
  const { data } = useGet(apiUrls.admin.organizations(), {
    staleTime: 0,
    params: {
      include_program_mapping: true,
      keep_related: true,
    },
  });

  const organizations = data?.data || [];
  return (
    <>
      <Typography variant="h6" fontWeight="bold" color="primary" mb={1}>
        Assign Programs to Organizations
      </Typography>
      {organizations.map((org, i) => (
        <OrganizationAccordionItem organization={org} key={i} />
      ))}
    </>
  );
}
